import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import api from '../lib/api';
import { 
  Star, 
  CheckCircle, 
  Crown,
  ArrowRight
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const Pricing = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState({});

  const tiers = [
    {
      id: 'basic',
      name: 'Basic',
      price: 0,
      description: 'Get started with basic trading tools',
      features: [
        'View limited trading signals',
        'Basic TradingView charts',
        'Access to basic course',
        'Community forum access'
      ],
      accent: 'silver',
      borderClass: 'border-[#C0C0C0]/20',
      badgeClass: 'bg-zinc-800 text-zinc-400'
    },
    {
      id: 'premium',
      name: 'Premium',
      price: 25,
      description: 'Perfect for serious traders',
      features: [
        'All trading signals',
        'Advanced TradingView charts',
        'Access to 5+ courses',
        'Shop discounts (10%)',
        'Email support',
        'Weekly market analysis'
      ],
      accent: 'white',
      borderClass: 'border-white/20',
      badgeClass: 'bg-white/10 text-white',
      popular: true
    },
    {
      id: 'elite',
      name: 'Elite',
      price: 50,
      description: 'The ultimate trading experience',
      features: [
        'Everything in Premium',
        'All courses unlocked',
        'Live trading room access',
        'Priority support',
        'Exclusive merchandise',
        'Daily market analysis',
        '1-on-1 monthly coaching call'
      ],
      accent: 'gold',
      borderClass: 'border-[#D4AF37]/30',
      badgeClass: 'bg-[#D4AF37]/20 text-[#D4AF37]'
    }
  ];

  const handleSubscribe = async (tierId) => {
    if (!isAuthenticated) {
      navigate('/register');
      return;
    }

    if (tierId === 'basic') {
      toast.info('You already have Basic access');
      return;
    }

    setLoading(prev => ({ ...prev, [tierId]: true }));
    try {
      const response = await api.post('/checkout/subscription', {
        origin_url: window.location.origin,
        subscription_tier: tierId
      });

      // Redirect to Stripe Checkout
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
      setLoading(prev => ({ ...prev, [tierId]: false }));
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] py-16" data-testid="pricing-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <h1 className="font-outfit text-4xl sm:text-5xl font-light text-white mb-4">
            Choose Your <span className="text-[#D4AF37]">Trading Level</span>
          </h1>
          <p className="text-zinc-400 max-w-2xl mx-auto text-lg">
            Start for free and upgrade as you grow. All plans include access to our trading community.
          </p>
        </motion.div>

        {/* Current Plan Badge */}
        {isAuthenticated && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-[#121212] border border-white/10 text-sm">
              <Crown className="w-4 h-4 text-[#D4AF37]" weight="duotone" />
              Current Plan: <span className="text-[#D4AF37] font-medium capitalize">{user?.subscription_tier}</span>
            </span>
          </motion.div>
        )}

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {tiers.map((tier, index) => {
            const isCurrentPlan = user?.subscription_tier === tier.id;
            const isUpgrade = tier.price > 0 && (
              !user?.subscription_tier || 
              (user?.subscription_tier === 'basic') ||
              (user?.subscription_tier === 'premium' && tier.id === 'elite')
            );

            return (
              <motion.div
                key={tier.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`relative bg-[#121212] border p-8 card-hover ${tier.borderClass} ${
                  tier.accent === 'gold' ? 'gold-glow' : ''
                }`}
                data-testid={`pricing-card-${tier.id}`}
              >
                {tier.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#D4AF37] text-black text-xs font-bold px-4 py-1">
                    MOST POPULAR
                  </div>
                )}

                {isCurrentPlan && (
                  <div className="absolute -top-3 right-4 bg-green-500 text-black text-xs font-bold px-3 py-1">
                    CURRENT
                  </div>
                )}

                <div className="text-center mb-8">
                  <span className={`text-xs font-mono uppercase px-2 py-1 ${tier.badgeClass}`}>
                    {tier.name}
                  </span>
                  <div className="mt-4 flex items-baseline justify-center gap-1">
                    {tier.price === 0 ? (
                      <span className="text-5xl font-outfit font-semibold text-white">Free</span>
                    ) : (
                      <>
                        <span className="text-2xl text-zinc-400">$</span>
                        <span className="text-5xl font-outfit font-semibold text-white">{tier.price}</span>
                        <span className="text-zinc-500">/mo</span>
                      </>
                    )}
                  </div>
                  <p className="text-sm text-zinc-500 mt-2">{tier.description}</p>
                </div>

                <ul className="space-y-4 mb-8">
                  {tier.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-3 text-sm">
                      <CheckCircle 
                        className={`w-5 h-5 flex-shrink-0 ${
                          tier.accent === 'gold' ? 'text-[#D4AF37]' : 'text-green-500'
                        }`} 
                        weight="fill" 
                      />
                      <span className="text-zinc-300">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  onClick={() => handleSubscribe(tier.id)}
                  disabled={isCurrentPlan || loading[tier.id]}
                  className={`w-full py-6 ${
                    tier.accent === 'gold'
                      ? 'bg-gradient-to-r from-[#D4AF37] to-[#B38F24] text-black hover:shadow-[0_0_30px_rgba(212,175,55,0.5)]'
                      : tier.price === 0
                      ? 'bg-zinc-800 text-white hover:bg-zinc-700'
                      : 'bg-white text-black hover:bg-zinc-200'
                  } ${isCurrentPlan ? 'opacity-50 cursor-not-allowed' : ''}`}
                  data-testid={`subscribe-${tier.id}`}
                >
                  {loading[tier.id] ? (
                    <span className="animate-spin w-5 h-5 border-2 border-current border-t-transparent rounded-full" />
                  ) : isCurrentPlan ? (
                    'Current Plan'
                  ) : tier.price === 0 ? (
                    isAuthenticated ? 'Current Plan' : 'Get Started'
                  ) : (
                    <>
                      {isUpgrade ? 'Upgrade Now' : 'Subscribe'}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </motion.div>
            );
          })}
        </div>

        {/* FAQ Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mt-20"
        >
          <h2 className="font-outfit text-2xl text-white text-center mb-8">
            Frequently Asked Questions
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {[
              {
                q: 'Can I cancel anytime?',
                a: 'Yes, you can cancel your subscription at any time. Your access will continue until the end of your billing period.'
              },
              {
                q: 'What payment methods do you accept?',
                a: 'We accept all major credit cards through our secure Stripe payment system.'
              },
              {
                q: 'Can I upgrade or downgrade my plan?',
                a: 'Yes, you can change your plan at any time. Upgrades take effect immediately, downgrades at the next billing cycle.'
              },
              {
                q: 'Do you offer refunds?',
                a: 'We offer a 7-day money-back guarantee for all paid subscriptions.'
              }
            ].map((faq) => (
              <div key={faq.q} className="bg-[#121212] border border-white/5 p-6">
                <h3 className="font-outfit text-white font-medium mb-2">{faq.q}</h3>
                <p className="text-sm text-zinc-400">{faq.a}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Pricing;
